import {
  FileEntity,
  SpaceTrackRow,
  Track,
} from "@highnote/server/src/core/entities";
import { TrackProviderRaw, useTrackRaw } from "App/components/useEntities";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { UPLOAD_GROUP, useFiles } from "../useFiles";
import { highnote } from "@highnote/server/src/sdk";
import {
  getDefaultVersionId,
  getLimitedPinnedVersions,
} from "@highnote/server/src/core/shared-util";
import { useUrlContext } from "App/routes/Main/useUrlContext";
import { useSpaceContext } from "App/common/useSpace";
import { ViewingSpaceId } from "App/modules/viewingSpace";
import {
  applyRowAliases,
  buildSpaceTrackTile,
} from "App/store/tracks/buildSpaceTrackTile";
import { useSpaceTrackRow } from "App/store/spaceTracks/useSpaceTrackRow";
import { useSpaceTrackRowsLoading } from "App/store/spaceTracks/useSpaceTrackRowsLoading";

type TrackContextValue = {
  track: Track;
  trackLoading: boolean;
  /** True when `track` was built from a `space-tracks-v1` row because this
   *  space BORROWS the track. Distinct from a tile's `isRowBacked`, which is
   *  true for a space's own tracks too. */
  isBorrowed: boolean;
  trackVersions: FileEntity[];
  trackFilesLoading: boolean;
  currentTrackVersion: FileEntity;
  setTrackVersions: (versions: FileEntity[]) => void;
  setCurrentTrackVersion: (version: FileEntity) => void;
  trackArtworkUrl: string;
  refetchTrackFiles: () => void;
  setTrackArtworkUrl: (url: string) => void;
  pinnedTrackVersions: string[];
  setPinnedTrackVersions: (pinnedVersions: string[]) => void;
  viewingSpaceId?: ViewingSpaceId;
};

const TrackContext = createContext<TrackContextValue>({
  track: undefined,
  trackVersions: [],
  currentTrackVersion: undefined,
  trackFilesLoading: true,
  setTrackVersions: () => undefined,
  setCurrentTrackVersion: () => undefined,
  trackArtworkUrl: undefined,
  refetchTrackFiles: () => undefined,
  setTrackArtworkUrl: () => undefined,
  trackLoading: true,
  isBorrowed: false,
  pinnedTrackVersions: [],
  setPinnedTrackVersions: () => undefined,
  viewingSpaceId: undefined,
});

// `spaceId` here is the VIEWING space, never the track's own origin
// (`track.spaceId`) — sending the origin back would make the server's
// row check always compare a space against itself.
export const getTrackFiles = async (
  track: Track,
  spaceId?: ViewingSpaceId,
  isRowBacked = false,
) => {
  // A row-backed track has no canonical document a borrower can read, so
  // GET_TRACK would deny it. The ids come from the row; GET_FILES with the
  // viewing space proves the pair through that space's row.
  //
  // A row written before the content mirror existed carries no ids at all
  // (`versionFileIds` and friends absent). That yields an empty `fileIds`,
  // no call, and an empty version list — silently. The backfill re-run gives
  // every row the mirror, which is what closes this case.
  let files: FileEntity[];
  if (isRowBacked) {
    const fileIds = [
      ...(track.versionFilesV2 || []),
      ...(track.files || []),
      ...(track.artworkFile ? [track.artworkFile] : []),
    ];
    files = fileIds.length
      ? await highnote.getFiles({
          ids: fileIds,
          ...(spaceId ? { spaceId } : {}),
        })
      : [];
  } else {
    files =
      (
        await highnote.getTrack({
          id: track.id,
          ...(spaceId ? { spaceId } : {}),
        })
      ).files || [];
  }

  // GET_TRACK returns the TRACK's files. A per-space artwork is a
  // SPACE_ATTACHMENT of the viewing space (decision 7), so it is not among
  // them; fetch it by id through the same space, whose SPACE_ATTACHMENT arm
  // authorizes it.
  let artworkFile = files.find((f) => f.id === track.artworkFile);
  if (!artworkFile && track.artworkFile && spaceId) {
    // Isolate this fallback: a failure here must not reject the whole
    // getTrackFiles chain, which would also wipe the version/attachment
    // state that useTrackFiles's shared .catch clears on any rejection.
    try {
      const [spaceArtwork] = await highnote.getFiles({
        ids: [track.artworkFile],
        spaceId,
      });
      artworkFile = spaceArtwork;
    } catch {
      artworkFile = undefined;
    }
  }

  const versionFiles = (track.versionFilesV2 || [])
    .map((fileId) => files.find((f) => f.id === fileId))
    .filter((f) => !!f);

  const attachmentFiles = (track.files || [])
    .map((fileId) => files.find((f) => f.id === fileId))
    .filter((f) => !!f);

  return { artworkFile, versionFiles, attachmentFiles };
};

/**
 * useTrackFiles can be used outside of the TrackContextProvider to fetch
 * files for a track.
 *
 * `viewingSpaceId` is the space the TRACK is being viewed from, and it is
 * the caller's to supply — this hook does not read the route. The route is
 * only right when the render is scoped to the route's own track (the track
 * page). The version editor is mounted once in Main and opened from the
 * player with whatever is playing; a table row in a move/add dialog lists
 * tracks from OTHER spaces. For those, the route's space is a space the
 * track is not in, and the server hard-denies a wrong viewing space where
 * it would have allowed no viewing space at all (origin fallback). So:
 * pass the space the track is listed under, or pass nothing.
 */
export const useTrackFiles = (
  track: Track,
  viewingSpaceId?: ViewingSpaceId,
  isRowBacked = false,
) => {
  const { versionIdPath } = useUrlContext();
  const { getDownloadUrl } = useFiles();
  const [loading, setLoading] = useState<boolean>(true);
  const [artwork, setArtwork] = useState<FileEntity>();
  const [versions, setVersions] = useState<FileEntity[]>([]);
  const [attachments, setAttachments] = useState<FileEntity[]>([]);
  const [artworkUrl, setArtworkUrl] = useState<string>();

  const resetState = () => {
    setArtwork(undefined);
    setVersions([]);
    setAttachments([]);
  };

  const setFiles = useCallback(() => {
    setLoading(true);
    getTrackFiles(track, viewingSpaceId, isRowBacked)
      .then(({ artworkFile, versionFiles, attachmentFiles }) => {
        setArtwork(artworkFile);
        setVersions(versionFiles);
        setAttachments(attachmentFiles);
        getDownloadUrl(artworkFile, viewingSpaceId).then((url) => {
          setArtworkUrl(url);
        });
      })
      // A denial (GET_TRACK masks it as not-found) must not leave the
      // PREVIOUS track's versions on screen, nor reject unhandled.
      .catch(() => {
        resetState();
        setArtworkUrl(undefined);
      })
      .finally(() => setLoading(false));
  }, [track, viewingSpaceId, isRowBacked]);

  // The track document updates for reasons unrelated to its files (e.g. the
  // comment summary denormalized onto it whenever someone comments). Refetching
  // on every write flickers `loading`, which transiently wipes
  // currentTrackVersion downstream and interrupts audio playback, so only
  // refetch when the file references change or the file pipeline bumps
  // fileUpdatedAt (how the server signals that a file doc itself changed).
  const trackFileIds = JSON.stringify([
    track?.artworkFile,
    track?.versionFilesV2,
    track?.files,
    track?.fileUpdatedAt,
  ]);

  useEffect(() => {
    if (!track) {
      resetState();
      setLoading(false);
      return;
    }

    setFiles();
    // `viewingSpaceId` is a dependency in its own right: the same track
    // viewed from another space is a different fetch.
  }, [track?.id, trackFileIds, viewingSpaceId, isRowBacked]);

  const currentTrackPageVersion = useMemo(() => {
    if (!versionIdPath || versions.length === 0) return undefined;
    return versions.find((v) => v.id === versionIdPath);
  }, [versionIdPath, versions, track?.id]);

  return {
    refetchTrackFiles: setFiles,
    artwork,
    artworkUrl,
    versions,
    attachments,
    loading,
    currentTrackPageVersion,
  };
};

const _TrackContextProvider = ({
  children,
  spaceId,
  row,
  isBorrowedRow,
  rowsPending,
}: {
  children: React.ReactNode;
  /**
   * The VIEWING space, already resolved by `TrackContextProvider` (the
   * opener's explicit space, else the route's). It arrives as a prop so the
   * two providers cannot derive it differently.
   */
  spaceId?: ViewingSpaceId;
  row?: SpaceTrackRow;
  isBorrowedRow: boolean;
  rowsPending: boolean;
}) => {
  const { getDownloadUrl, removeUpload } = useFiles();
  const { entity: rawTrack, loading: rawTrackLoading } = useTrackRaw();
  // Only for the plan's pinned-version limit, below. The viewing space comes
  // from the prop, never from a second read of this context.
  const { space } = useSpaceContext();
  // Row first (design §5.5). A borrowed track IS its row; the canonical
  // document is origin-only under the rules and never reaches this client.
  const rowTrack = useMemo(
    () =>
      isBorrowedRow && row
        ? (buildSpaceTrackTile({
            row,
            viewingSpaceId: spaceId,
          }) as unknown as Track)
        : undefined,
    [isBorrowedRow, row, spaceId],
  );
  // The viewing space's row is the truth for title/description/artistName/
  // artworkFile (decision 3, 2026-09-07). Every consumer of `track` below —
  // the page header, the editor's initial state, the media-session
  // metadata — sees the alias, not the canonical value.
  //
  // Guard 1: `applyRowAliases({}, row)` would turn an ABSENT track into a
  // truthy object with no id, and every `if (!track)` below would misfire.
  //
  // Guard 2: the row is looked up by the ROUTE's id, while `rawTrack` is
  // whatever the canonical listener last delivered — it keeps the PREVIOUS
  // track's document until the new snapshot arrives. On canonical-to-
  // canonical navigation the two disagree for one render, and the
  // DESTINATION row's title/artist/artwork must not land on the previous
  // track's document. Apply the aliases only to the row's own track.
  const track = useMemo(() => {
    if (rowTrack) return rowTrack;
    if (!rawTrack) return rawTrack;
    return row?.trackId === rawTrack.id
      ? applyRowAliases(rawTrack, row)
      : rawTrack;
  }, [rowTrack, rawTrack, row]);
  const isBorrowed = Boolean(rowTrack);
  const trackLoading = isBorrowed ? false : rowsPending || rawTrackLoading;
  const {
    versions,
    artwork,
    refetchTrackFiles,
    loading: trackFilesLoading,
    currentTrackPageVersion,
  } = useTrackFiles(track, spaceId, isBorrowed);
  const [pinnedTrackVersions, _setPinnedTrackVersions] = useState<string[]>([]);
  const defaultVersionId = getDefaultVersionId(track);
  const [trackVersions, setTrackVersions] = useState<FileEntity[]>(versions);
  const [currentTrackVersion, setCurrentTrackVersion] = useState<FileEntity>();
  const [trackArtworkUrl, setTrackArtworkUrl] = useState<string>();
  const unmountedRef = useRef<boolean>(false);
  const { getUploadCache, getUploads } = useFiles();
  const uploadCachePinned = getUploadCache(
    UPLOAD_GROUP.TRACK_VERSIONS_PINNED,
    track?.id,
  );
  const uploadCacheUnpinned = getUploadCache(
    UPLOAD_GROUP.TRACK_VERSIONS_UNPINNED,
    track?.id,
  );

  const setPinnedTrackVersions = useCallback(
    (pinnedVersions: string[]) => {
      _setPinnedTrackVersions(
        getLimitedPinnedVersions(pinnedVersions, space?.subscriptionTier),
      );
    },
    [space?.subscriptionTier],
  );

  useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    [],
  );

  useEffect(() => {
    if (!track || trackFilesLoading) {
      setTrackVersions([]);
      setCurrentTrackVersion(undefined);
      setPinnedTrackVersions([]);
      return;
    }

    const versionFileIds = track?.versionFilesV2 || [];
    const _trackVersions = versionFileIds.map((fileId) => {
      const downloaded = versions.find((v) => v.id === fileId);
      if (downloaded) return downloaded;

      const uploadsPinned = getUploads({ cache: uploadCachePinned });
      const uploadsUnpinned = getUploads({ cache: uploadCacheUnpinned });
      const upload = [...uploadsPinned, ...uploadsUnpinned].find(
        (u) => u.file.id === fileId,
      );

      return {
        createdAt: 0,
        createdBy: "",
        id: fileId,
        name: "[File Not Found]",
        ...(upload?.file || {}),
      };
    });
    const defaultVersion = _trackVersions.find(
      (v) => v.id === defaultVersionId,
    );

    let _pinnedTrackVersions = (track.pinnedVersionFiles || []).filter((id) =>
      versionFileIds.includes(id),
    );
    if (_pinnedTrackVersions.length < 1 && defaultVersionId) {
      _pinnedTrackVersions = [defaultVersionId];
    }

    setTrackVersions(_trackVersions);
    setPinnedTrackVersions(_pinnedTrackVersions);
    setCurrentTrackVersion(currentTrackPageVersion ?? defaultVersion);
    _trackVersions.forEach((v) => removeUpload(v.id));
  }, [
    track,
    trackFilesLoading,
    versions,
    defaultVersionId,
    currentTrackPageVersion,
    space?.subscriptionTier,
  ]);

  useEffect(() => {
    if (!artwork) {
      setTrackArtworkUrl(undefined);
      return;
    }

    // `spaceId` here is the VIEWING space (from useSpaceContext, which itself
    // re-exports useUrlContext's spaceId) — never track.spaceId, the origin.
    getDownloadUrl(artwork, spaceId).then((url) => {
      if (unmountedRef.current) return;
      setTrackArtworkUrl(url);
    });
  }, [artwork, spaceId]);

  const value = useMemo(
    () => ({
      track,
      trackLoading,
      isBorrowed,
      trackArtworkUrl,
      setTrackArtworkUrl,
      trackVersions,
      trackFilesLoading,
      currentTrackVersion,
      refetchTrackFiles,
      setCurrentTrackVersion,
      setTrackVersions,
      pinnedTrackVersions,
      setPinnedTrackVersions,
      viewingSpaceId: spaceId,
    }),
    [
      track,
      trackLoading,
      isBorrowed,
      trackArtworkUrl,
      setTrackArtworkUrl,
      trackVersions,
      trackFilesLoading,
      currentTrackVersion,
      refetchTrackFiles,
      setCurrentTrackVersion,
      setTrackVersions,
      pinnedTrackVersions,
      spaceId,
    ],
  );

  return (
    <TrackContext.Provider value={value}>{children}</TrackContext.Provider>
  );
};

export const TrackContextProvider = ({
  id,
  viewingSpaceId,
  children,
}: {
  id: Id;
  viewingSpaceId?: ViewingSpaceId;
  children: React.ReactNode;
}) => {
  const { space, spaceError, spaceId: routeSpaceId } = useSpaceContext();
  const spaceId = viewingSpaceId ?? routeSpaceId;
  const row = useSpaceTrackRow(spaceId, id);
  const rowsLoading = useSpaceTrackRowsLoading(spaceId);
  const isBorrowedRow = Boolean(row && row.originSpaceId !== row.spaceId);
  // A failed space read. `useSpace` models one as `space` undefined WITH
  // `spaceError` set, and its own loading test is `!space && !spaceError` —
  // so `!space` alone never clears, and neither does `rowsLoading`, because
  // `useSpace` attaches the row watcher only when the space read SUCCEEDS
  // and a missing watcher now reads as "not yet answered"
  // (useSpaceTrackRowsLoading). Both terms have to give way here, or a
  // failed read pins the gate — and the page's loading state — for good.
  const spaceReadFailed = Boolean(spaceError);
  // On the space route, wait for the rows before the canonical listener
  // opens. A borrowed track's canonical document is origin-only, and a
  // listener a borrower opens errors with permission-denied instead of an
  // answer. Two things are still in flight on a cold load: the space
  // document (`!space`), and the row watcher its arrival attaches
  // (`rowsLoading`, true while no watcher exists yet).
  // An explicit viewing space (the version editor) skips the wait: its
  // row is in the store or it is not, and the store may hold no watcher.
  const routeRowsPending =
    !viewingSpaceId &&
    Boolean(routeSpaceId) &&
    !row &&
    !spaceReadFailed &&
    (!space || rowsLoading);
  const canonicalId = isBorrowedRow || routeRowsPending ? undefined : id;

  if (!id) return <>{children}</>;

  return (
    <TrackProviderRaw id={canonicalId}>
      <_TrackContextProvider
        spaceId={spaceId}
        row={row}
        isBorrowedRow={isBorrowedRow}
        rowsPending={routeRowsPending}
      >
        {children}
      </_TrackContextProvider>
    </TrackProviderRaw>
  );
};

export const useTrack = () => useContext(TrackContext);
